/**
 * Displays a metric: label, value, optional trend, optional period-over-period fields.
 * Use {@link MetricProps.variant} `"delta"` for inline current vs previous + pill delta (MetricDelta-style layout).
 */
import { TrendDownIcon, TrendUpIcon } from "@phosphor-icons/react";

import { cn } from "@/lib/utils";

export type MetricVariant = "default" | "delta";

export interface MetricProps {
  /** `"default"`: value + icon row, then one comparison line. `"delta"`: current + `from` previous on one row, then icon + delta pill. */
  variant?: MetricVariant | null;
  /** Title shown above the value (`text-xs`, muted). */
  label: string;
  /** Primary value (`text-base` bold in default; `text-2xl` in delta variant). */
  value: string;
  format?: "currency" | "percent" | "number" | null;
  trend?: "up" | "down" | "neutral" | null;
  /** When true, flip which direction reads as positive vs negative (e.g. up in negative coverage is bad). */
  invertTrend?: boolean | null;
  /** Optional line below comparison / delta row (`text-[10px]`). */
  subtitle?: string | null;
  /** Previous period value (e.g. "234"). Shown in comparison line or inline `from` in delta variant. */
  previousValue?: string | null;
  /** Change from previous period (e.g. "+78"). */
  delta?: string | null;
  /** Percent change (e.g. "+33.3%"). */
  deltaPercent?: string | null;
}

const formatMetricValue = (
  value: string,
  format: MetricProps["format"],
): string => {
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return value;
  if (format === "currency") return `$${num.toLocaleString()}`;
  if (format === "percent") return `${num}%`;
  return value;
};

/** Builds period-over-period line: "from 234, +78 (+33.3%)" when previousValue/delta/deltaPercent are set. */
const formatComparisonLine = (
  previousValue?: string | null,
  delta?: string | null,
  deltaPercent?: string | null,
): string | null => {
  const parts: string[] = [];
  if (previousValue != null && previousValue !== "")
    parts.push(`from ${previousValue}`);
  if (delta != null && delta !== "") parts.push(delta);
  if (deltaPercent != null && deltaPercent !== "")
    parts.push(`(${deltaPercent})`);
  if (parts.length === 0) return null;
  return parts.join(", ");
};

const Metric = ({
  variant = "default",
  label,
  value,
  format = null,
  trend = null,
  invertTrend = false,
  subtitle = null,
  previousValue = null,
  delta = null,
  deltaPercent = null,
}: MetricProps) => {
  const effectiveVariant = variant ?? "default";
  const displayValue =
    typeof value === "string" ? formatMetricValue(value, format) : value;

  if (effectiveVariant === "delta") {
    const isUp = trend === "up";
    const isDown = trend === "down";
    const hasTrend = isUp || isDown;
    const inverted = invertTrend === true;
    const trendIsPositive = inverted ? isDown : isUp;
    const trendIsNegative = inverted ? isUp : isDown;
    const RawTrendIcon = isUp ? TrendUpIcon : isDown ? TrendDownIcon : null;
    const iconColor = trendIsPositive
      ? "text-primary"
      : trendIsNegative
        ? "text-chart-5"
        : "";
    const badgeBg = trendIsPositive
      ? "bg-primary/10 text-primary"
      : trendIsNegative
        ? "bg-chart-5/10 text-chart-5"
        : "bg-muted text-muted-foreground";
    const showDeltaRow = Boolean(
      (delta != null && delta !== "") ||
        (deltaPercent != null && deltaPercent !== "") ||
        hasTrend,
    );

    return (
      <div className="flex flex-col gap-1">
        {label ? (
          <span className="text-sm text-muted-foreground">{label}</span>
        ) : null}
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-2xl font-bold text-foreground">
            {displayValue}
          </span>
          {previousValue != null && previousValue !== "" ? (
            <span className="text-base text-muted-foreground">
              from {previousValue}
            </span>
          ) : null}
        </div>
        {showDeltaRow ? (
          <div className="flex items-center gap-2">
            {RawTrendIcon != null ? (
              <RawTrendIcon
                className={cn("size-4 shrink-0", iconColor)}
                weight="bold"
              />
            ) : null}
            {(delta != null && delta !== "") ||
            (deltaPercent != null && deltaPercent !== "") ? (
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium",
                  badgeBg,
                )}
              >
                {delta != null && delta !== "" ? delta : ""}
                {delta != null &&
                delta !== "" &&
                deltaPercent != null &&
                deltaPercent !== ""
                  ? " "
                  : ""}
                {deltaPercent != null && deltaPercent !== ""
                  ? `(${deltaPercent})`
                  : ""}
              </span>
            ) : null}
          </div>
        ) : null}
        {subtitle ? (
          <span className="text-xs text-muted-foreground">{subtitle}</span>
        ) : null}
      </div>
    );
  }

  const effectiveTrend =
    invertTrend && (trend === "up" || trend === "down")
      ? trend === "up"
        ? "down"
        : "up"
      : trend;
  const TrendIcon =
    effectiveTrend === "up"
      ? TrendUpIcon
      : effectiveTrend === "down"
        ? TrendDownIcon
        : null;
  const trendColor =
    effectiveTrend === "up"
      ? "text-primary"
      : effectiveTrend === "down"
        ? "text-chart-5"
        : "";
  const comparisonLine = formatComparisonLine(
    previousValue,
    delta,
    deltaPercent,
  );

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <span className="text-sm text-muted-foreground">{label}</span>
      ) : null}
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-foreground">
          {displayValue}
        </span>
        {TrendIcon != null && (
          <TrendIcon
            className={cn("size-3 shrink-0", trendColor)}
            weight="bold"
          />
        )}
      </div>
      {comparisonLine ? (
        <span className="text-xs text-muted-foreground">{comparisonLine}</span>
      ) : null}
      {subtitle ? (
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      ) : null}
    </div>
  );
};

/** Item shape for {@link MetricGroup}: same as a single metric row. */
export interface MetricGroupItem {
  label: string;
  value: string;
  variant?: MetricProps["variant"];
  format?: MetricProps["format"];
  trend?: MetricProps["trend"];
  invertTrend?: boolean | null;
  subtitle?: string | null;
  previousValue?: string | null;
  delta?: string | null;
  deltaPercent?: string | null;
}

export interface MetricGroupProps {
  items?: MetricGroupItem[] | null;
}

/** Multiple metrics in one row (no section title, no card). */
const MetricGroup = ({ items }: MetricGroupProps) => {
  const metricItems = Array.isArray(items) ? items : [];

  return (
    <div className="flex w-full flex-wrap gap-8">
      {metricItems.map((item, i) => (
        <Metric
          // biome-ignore lint/suspicious/noArrayIndexKey: order is fixed per spec render
          key={i}
          variant={item.variant ?? "default"}
          label={item.label}
          value={item.value}
          format={item.format ?? null}
          trend={item.trend ?? null}
          invertTrend={item.invertTrend ?? null}
          subtitle={item.subtitle ?? null}
          previousValue={item.previousValue ?? null}
          delta={item.delta ?? null}
          deltaPercent={item.deltaPercent ?? null}
        />
      ))}
    </div>
  );
};

export { Metric, MetricGroup };
